import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Search, Send, Sparkles, FileText, Landmark, Paperclip, CheckCheck, MessageCircle } from "lucide-react";

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  }
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

export default function WhatsAppConnect() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // selected client
  const [messages, setMessages] = useState([]);
  const [banks, setBanks] = useState([]);
  const [search, setSearch] = useState("");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/clients"),
      api.get("/whatsapp/threads"),
      api.get("/banks"),
    ]);
    setClients(a.data);
    setThreads(b.data);
    setBanks(c.data);
  };
  useEffect(() => { load(); }, []);

  // Pick a client → load messages
  const selectClient = async (c) => {
    setActive(c);
    setInput("");
    const { data } = await api.get(`/whatsapp/threads/${c.id}/messages`);
    setMessages(data);
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const send = async (textOverride, attachmentLabel) => {
    if (!active) return;
    const text = (textOverride ?? input).trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/threads/send", { client_id: active.id, text, attachment_label: attachmentLabel || null });
      setMessages(m => [...m, data.message]);
      setInput("");
      if (data.sent_via === "cloud_api") {
        toast.success("Sent via WhatsApp Cloud API");
      } else if (data.wa_me_url) {
        window.open(data.wa_me_url, "_blank", "noopener,noreferrer");
        toast.success("Logged · WhatsApp Web opened. Click send there.");
      } else {
        toast.error("Client has no phone number");
      }
      load(); // refresh threads
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const smartMessage = async () => {
    if (!active) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/draft-email", {
        purpose: "Brief, professional WhatsApp message reminding client to share documents for pending GST/ITR filing",
        client_name: active.name,
        context: input || "",
      });
      // strip Subject line for whatsapp
      const msg = (data.email || "").replace(/^Subject:.*\n+/i, "").replace(/\n\n+/g, "\n\n");
      setInput(msg.trim());
      toast.success("AI draft ready");
    } catch (e) {
      toast.error("AI draft failed");
    } finally {
      setAiBusy(false);
    }
  };

  const shareBank = () => {
    const b = banks.find(x => x.is_default) || banks[0];
    if (!b) { toast.error("No bank account configured"); return; }
    const text = `Bank Details for Payment:\n\nBank: ${b.bank_name}\nA/c Holder: ${b.holder_name}\nA/c No: ${b.account_number}\nIFSC: ${b.ifsc}${b.upi_id ? `\nUPI: ${b.upi_id}` : ""}\n\nKindly share UTR / screenshot after payment.`;
    setInput(text);
  };

  const sharePartyStatement = async () => {
    if (!active) return;
    try {
      // Build a quick statement from invoices
      const { data: invs } = await api.get("/invoices");
      const mine = invs.filter(i => i.client_id === active.id);
      const total = mine.reduce((s, i) => s + (i.total || 0), 0);
      const received = mine.reduce((s, i) => s + (i.payment_received || 0), 0);
      const due = total - received;
      const lines = mine.slice(0, 8).map(i => `• #${i.invoice_number} (${i.issue_date}) — ₹${(i.total||0).toLocaleString("en-IN")} · Bal ₹${(i.balance||0).toLocaleString("en-IN")}`).join("\n");
      const text = `Party Statement — ${active.name}\n\n${lines || "No invoices yet."}\n\nTotal Billed: ₹${total.toLocaleString("en-IN")}\nReceived: ₹${received.toLocaleString("en-IN")}\nBalance Due: ₹${due.toLocaleString("en-IN")}\n\nRegards,\nTAXINPHO Office`;
      setInput(text);
    } catch (e) { toast.error("Could not build statement"); }
  };

  // Filter sidebar: union of clients + threads, threads first
  const sidebarItems = (() => {
    const ids = new Set(threads.map(t => t.client_id));
    const rest = clients.filter(c => !ids.has(c.id)).map(c => ({
      client_id: c.id, client_name: c.name, mobile: c.mobile || c.phone || "",
      last_text: "", last_at: null, count: 0,
    }));
    const all = [...threads, ...rest];
    if (!search) return all;
    const q = search.toLowerCase();
    return all.filter(t => t.client_name?.toLowerCase().includes(q) || t.mobile?.includes(q));
  })();

  return (
    <div className="flex" style={{ height: "calc(100vh - 0px)" }}>
      {/* Left rail */}
      <div className="w-80 border-r border-zinc-200 bg-white flex flex-col">
        <div className="px-4 py-4 border-b border-zinc-200">
          <h2 className="font-display font-black text-2xl tracking-tighter">WhatsApp Connect</h2>
          <p className="text-xs text-zinc-500 mt-1">{threads.length} active · {clients.length} total clients</p>
        </div>
        <div className="px-3 py-3 border-b border-zinc-200">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search or start a new chat" className="w-full bg-zinc-100 pl-9 pr-3 py-2 text-sm focus:outline-none" data-testid="wa-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {sidebarItems.length === 0 && <p className="p-6 text-xs text-zinc-500 font-mono text-center">[ no clients ]</p>}
          {sidebarItems.map(t => (
            <button
              key={t.client_id}
              onClick={() => selectClient({ id: t.client_id, name: t.client_name, mobile: t.mobile })}
              data-testid={`wa-thread-${t.client_id}`}
              className={`w-full text-left px-4 py-3 border-b border-zinc-100 flex items-start gap-3 hover:bg-zinc-50 ${active?.id === t.client_id ? "bg-zinc-100" : ""}`}
            >
              <div className="w-10 h-10 bg-[#1ABEDC] text-zinc-900 flex items-center justify-center font-bold rounded-full shrink-0">{t.client_name?.[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-semibold text-sm truncate">{t.client_name}</span>
                  {t.last_at && <span className="text-[10px] text-zinc-500 shrink-0">{fmtTime(t.last_at)}</span>}
                </div>
                <div className="text-xs text-zinc-500 truncate">{t.last_text || (t.mobile ? `+${t.mobile}` : "No phone")}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col bg-[#E5DDD5]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill='%23d4cdc3' fill-opacity='0.4'%3E%3Cpath d='M30 30c0-11 9-20 20-20v40c-11 0-20-9-20-20zM30 30c0 11-9 20-20 20V10c11 0 20 9 20 20z'/%3E%3C/g%3E%3C/svg%3E\")" }}>
        {!active ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md p-8 bg-white/80 backdrop-blur border border-zinc-200">
              <MessageCircle size={36} className="mx-auto mb-4 text-[#25D366]" />
              <h3 className="font-display font-bold text-2xl tracking-tight mb-2">WhatsApp Connect</h3>
              <p className="text-sm text-zinc-600 mb-4">Pick a client from the left to start chatting. Every message you send is logged here per client.</p>
              <p className="text-xs text-zinc-500">Tip: enable Meta WhatsApp Business Cloud API in <code className="font-mono bg-zinc-100 px-1">backend/.env</code> for one-click auto-send. Without it, clicking Send opens WhatsApp Web with your message pre-filled.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 bg-[#F0F2F5] border-b border-zinc-300 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1ABEDC] text-zinc-900 flex items-center justify-center font-bold rounded-full">{active.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1">
                <div className="font-semibold">{active.name}</div>
                <div className="text-xs text-zinc-500 font-mono">{active.mobile ? `+${active.mobile.replace(/\D/g,"").replace(/^91/, "91 ")}` : "No phone"}</div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 scrollbar-thin">
              {messages.length === 0 && (
                <div className="text-center text-xs text-zinc-600 my-8 font-mono">[ no messages yet — start the conversation ]</div>
              )}
              {messages.map(m => (
                <div key={m.id} className="flex justify-end mb-2">
                  <div className="max-w-[70%] bg-[#DCF8C6] px-3 py-2 shadow-sm rounded-lg">
                    {m.attachment_label && (
                      <div className="text-xs flex items-center gap-1.5 mb-1 px-2 py-1 bg-white/60 border border-zinc-200">
                        <Paperclip size={11} /> {m.attachment_label}
                      </div>
                    )}
                    <pre className="whitespace-pre-wrap font-sans text-sm">{m.text}</pre>
                    <div className="flex items-center justify-end gap-1 text-[10px] text-zinc-500 mt-1">
                      <span>{fmtTime(m.sent_at)}</span>
                      <CheckCheck size={12} className={m.status === "sent" ? "text-[#34B7F1]" : ""} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick actions */}
            <div className="px-3 py-2 bg-[#F0F2F5] border-t border-zinc-300 flex flex-wrap gap-2">
              <button onClick={smartMessage} disabled={aiBusy} data-testid="wa-smart" className="text-xs bg-white border border-zinc-300 px-3 py-1.5 hover:bg-zinc-50 flex items-center gap-1.5 disabled:opacity-50">
                <Sparkles size={12} className="text-[#002FA7]" /> {aiBusy ? "AI drafting..." : "Smart Messaging"}
              </button>
              <button onClick={sharePartyStatement} data-testid="wa-statement" className="text-xs bg-white border border-zinc-300 px-3 py-1.5 hover:bg-zinc-50 flex items-center gap-1.5">
                <FileText size={12} className="text-[#D92D20]" /> Party Statement
              </button>
              <button onClick={shareBank} data-testid="wa-bank" className="text-xs bg-white border border-zinc-300 px-3 py-1.5 hover:bg-zinc-50 flex items-center gap-1.5">
                <Landmark size={12} className="text-[#002FA7]" /> Share Bank Details
              </button>
            </div>

            {/* Composer */}
            <div className="px-3 py-3 bg-[#F0F2F5] border-t border-zinc-300 flex items-end gap-2">
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type a Message"
                rows={Math.min(4, (input.match(/\n/g) || []).length + 1)}
                data-testid="wa-input"
                className="flex-1 bg-white px-3 py-2 text-sm focus:outline-none resize-none"
              />
              <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="wa-send" className="bg-[#25D366] text-white p-3 hover:bg-green-600 disabled:opacity-50">
                <Send size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
